'use client';

import {
	BN,
	calculateLongShortFundingRate,
	convertToNumber,
} from '@velocity-exchange/sdk';
import { useCallback, useEffect, useState } from 'react';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useUiUpdateInterval from './useUiUpdateInterval';
import { MarketId } from '@velocity-exchange/common';
import { usePerpMarketsLookup } from './perpMarkets/usePerpMarketsLookup';
import {
	getLiveMarkPrice,
	getLiveMmOracleData,
	getLivePerpMarketInfo,
	getLiveRawOracleData,
	publishPredictedFundings,
	toRawMmOracleData,
} from 'src/stores/marketLiveState';

type MarketFundingRate = {
	marketIndex: number;
	fundingRate: number;
};

const areFundingsEqual = (a: MarketFundingRate[], b: MarketFundingRate[]) => {
	if (a.length !== b.length) return false;

	for (let i = 0; i < a.length; i++) {
		if (
			a[i].marketIndex !== b[i].marketIndex ||
			a[i].fundingRate !== b[i].fundingRate
		)
			return false;
	}

	return true;
};

/**
 * Uses trading history from the history server to calculate price change
 * @returns
 */
const usePredictedFunding = () => {
	const velocityClientIsReady = useVelocityClientIsReady();

	const [currentPredictedFunding, setCurrentPredictedFunding] = useState<
		MarketFundingRate[]
	>([]);
	const perpMarketsLookup = usePerpMarketsLookup();

	const updatePredictedFunding = useCallback(() => {
		if (!velocityClientIsReady) return;

		const fundings: MarketFundingRate[] = [];

		perpMarketsLookup.forEach((market) => {
			if (!market) return;

			const marketId = MarketId.createPerpMarket(market.marketIndex);
			const currentMarkPrice = getLiveMarkPrice(marketId);
			const oraclePriceData = getLiveRawOracleData(marketId);
			const mmOraclePrice = getLiveMmOracleData(marketId);
			const perpMarketAccount = getLivePerpMarketInfo(marketId);

			// Skip if we don't have the necessary data
			if (
				!currentMarkPrice ||
				currentMarkPrice.eqZero() ||
				!oraclePriceData ||
				!perpMarketAccount
			)
				return;

			const nowBN = new BN((Date.now() / 1000).toFixed(0));

			let fundingInfo = [new BN(0), new BN(0)];
			try {
				fundingInfo = calculateLongShortFundingRate(
					perpMarketAccount,
					toRawMmOracleData(mmOraclePrice),
					oraclePriceData,
					currentMarkPrice.val,
					nowBN
				);
			} catch {
				// noop
			}
			const longFundingRate = fundingInfo[0] ?? new BN(0);
			const shortFundingRate = fundingInfo[1] ?? new BN(0);

			const nextFundingRateNum = convertToNumber(
				longFundingRate.abs().gt(shortFundingRate.abs())
					? longFundingRate
					: shortFundingRate
			);

			fundings.push({
				marketIndex: marketId.marketIndex,
				fundingRate: nextFundingRateNum,
			});
		});

		// Functional update so the equality check runs against the latest value:
		// this callback is memoized (and cached in useInterval's ref), so reading
		// `currentPredictedFunding` from the closure would compare against a stale
		// snapshot and republish every tick.
		setCurrentPredictedFunding((prev) =>
			areFundingsEqual(fundings, prev) ? prev : fundings
		);
	}, [velocityClientIsReady, perpMarketsLookup]);

	// publish into the live-state module when the local value changes
	useEffect(() => {
		publishPredictedFundings(currentPredictedFunding);
	}, [currentPredictedFunding]);

	// update price change
	useUiUpdateInterval(updatePredictedFunding, true, true);
};

export default usePredictedFunding;
